import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { MagnifyingGlass, Funnel, MapPin, Calendar, CaretDown, X, Crosshair, Bell, BellSlash, Heart, Star, Tag } from '@phosphor-icons/react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { getCategoryLabel, getCategoryColor, CATEGORY_MAP } from '../lib/categories';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { usePushNotifications } from '../hooks/usePushNotifications';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Custom map icons
const createIcon = (color) => L.divIcon({
  className: 'custom-marker',
  html: `<div style="width:32px;height:32px;background:${color};border:2px solid #004E89;border-radius:50% 50% 50% 0;transform:rotate(-45deg);display:flex;align-items:center;justify-content:center;box-shadow:2px 2px 0 #004E89"><div style="width:10px;height:10px;background:white;border-radius:50%;transform:rotate(45deg)"></div></div>`,
  iconSize: [32, 32],
  iconAnchor: [16, 32],
  popupAnchor: [0, -32],
});

const getMarkerIcon = (category) => createIcon(getCategoryColor(category));

function MapController({ center, zoom }) {
  const map = useMap();
  useEffect(() => {
    if (center) map.flyTo(center, zoom || 12, { duration: 1 });
  }, [center, zoom, map]);
  return null;
}

function SaleCard({ sale, onHover, isActive, isSaved, onToggleSave }) {
  return (
    <div
      className={`sale-card slide-in ${isActive ? 'ring-2 ring-[#FF6B35]' : ''}`}
      onMouseEnter={() => onHover(sale)}
      onMouseLeave={() => onHover(null)}
      data-testid={`sale-card-${sale.id}`}
    >
      <Link to={`/sale/${sale.id}`} className="block">
        {sale.photos?.length > 0 ? (
          <div className="w-full h-36 rounded-xl overflow-hidden border-2 border-[#004E89]">
            <img
              src={`${API}/files/${sale.photos[0]}`}
              alt={sale.title}
              className="w-full h-full object-cover"
              onError={(e) => { e.target.src = 'https://images.pexels.com/photos/4923440/pexels-photo-4923440.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940'; }}
            />
          </div>
        ) : (
          <div className="w-full h-36 rounded-xl overflow-hidden border-2 border-[#004E89] bg-gradient-to-br from-[#FFD166] to-[#FF6B35] flex items-center justify-center">
            <MapPin size={48} weight="duotone" color="#004E89" />
          </div>
        )}
      </Link>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className={`tag tag-${sale.category === 'business_closing' ? 'business' : sale.category}`}>
            {getCategoryLabel(sale.category)}
          </span>
          {sale.distance !== undefined && (
            <span className="text-xs text-[#5C7A99] font-medium">{sale.distance} mi</span>
          )}
        </div>
        {onToggleSave && (
          <button onClick={(e) => { e.preventDefault(); onToggleSave(sale.id); }}
            className="text-[#5C7A99] hover:text-red-500 transition-colors p-1"
            data-testid={`save-card-btn-${sale.id}`}
          >
            <Heart size={18} weight={isSaved ? 'fill' : 'bold'} className={isSaved ? 'text-red-500' : ''} />
          </button>
        )}
      </div>
      <Link to={`/sale/${sale.id}`} className="block">
        <h3 className="text-base font-bold text-[#004E89] line-clamp-1" style={{ fontFamily: 'Outfit' }}>{sale.title}</h3>
        <div className="flex items-center gap-1 text-xs text-[#5C7A99]">
          <MapPin size={14} weight="bold" />
          {sale.address_hidden ? (
            <span className="text-[#FF6B35] font-semibold">Address revealed at 6 AM sale day</span>
          ) : (
            <span>{sale.city}, {sale.state}</span>
          )}
        </div>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1 text-xs text-[#5C7A99]">
            <Calendar size={14} weight="bold" />
            {new Date(sale.start_date).toLocaleDateString()} - {new Date(sale.end_date).toLocaleDateString()}
          </div>
          {sale.avg_rating > 0 && (
            <div className="flex items-center gap-0.5">
              <Star size={12} weight="fill" className="text-[#FFD166]" />
              <span className="text-[10px] font-bold text-[#004E89]">{sale.avg_rating}</span>
            </div>
          )}
        </div>
        {sale.items_categories?.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-1" data-testid={`sale-tags-${sale.id}`}>
            {sale.items_categories.slice(0, 3).map(tag => (
              <span key={tag} className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-[#004E89]/8 text-[#004E89] border border-[#004E89]/15">{tag}</span>
            ))}
            {sale.items_categories.length > 3 && <span className="text-[9px] text-[#5C7A99] font-medium">+{sale.items_categories.length - 3}</span>}
          </div>
        )}
      </Link>
    </div>
  );
}

export default function MainApp() {
  const { user } = useAuth();
  const { isSupported: pushSupported, isSubscribed: pushSubscribed, subscribe: pushSubscribe } = usePushNotifications(user);
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('all');
  const [radius, setRadius] = useState(50);
  const [mapCenter, setMapCenter] = useState([39.8283, -98.5795]); // US center
  const [mapZoom, setMapZoom] = useState(4);
  const [hoveredSale, setHoveredSale] = useState(null);
  const [userLocation, setUserLocation] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [showTagFilter, setShowTagFilter] = useState(false);
  const [selectedTags, setSelectedTags] = useState([]);
  const [availableTags, setAvailableTags] = useState([]);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [savedIds, setSavedIds] = useState(new Set());
  const [searchParams] = useSearchParams();

  // Load notification settings and saved sale IDs
  useEffect(() => {
    // Load tags
    axios.get(`${API}/tags`).then(({ data }) => setAvailableTags(data.tags || [])).catch(() => {});
    if (user) {
      axios.get(`${API}/users/notification-settings`, { withCredentials: true })
        .then(({ data }) => setNotificationsEnabled(data.email_notifications))
        .catch(() => {});
      axios.get(`${API}/favorites/ids`, { withCredentials: true })
        .then(({ data }) => setSavedIds(new Set(data.sale_ids || [])))
        .catch(() => {});
    }
  }, [user]);

  const toggleSaveCard = async (saleId) => {
    if (!user) return;
    const wasSaved = savedIds.has(saleId);
    try {
      if (wasSaved) {
        await axios.delete(`${API}/favorites/${saleId}`, { withCredentials: true });
        setSavedIds(prev => { const n = new Set(prev); n.delete(saleId); return n; });
        toast.success('Removed from saved');
      } else {
        await axios.post(`${API}/favorites/${saleId}`, {}, { withCredentials: true });
        setSavedIds(prev => new Set(prev).add(saleId));
        toast.success('Saved!');
      }
    } catch { toast.error('Failed to update'); }
  };

  const fetchSales = useCallback(async (params = {}) => {
    setLoading(true);
    try {
      const queryParams = new URLSearchParams();
      if (params.category && params.category !== 'all') queryParams.set('category', params.category);
      if (params.search) queryParams.set('search', params.search);
      if (params.lat) queryParams.set('lat', params.lat);
      if (params.lng) queryParams.set('lng', params.lng);
      if (params.radius) queryParams.set('radius', params.radius);
      if (params.zip_code) queryParams.set('zip_code', params.zip_code);
      if (params.tags && params.tags.length > 0) queryParams.set('tags', params.tags.join(','));
      const { data } = await axios.get(`${API}/sales?${queryParams.toString()}`);
      setSales(data.sales || []);
    } catch (err) {
      console.error('Error fetching sales:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchSales(); }, [fetchSales]);

  const handleSearch = (e) => {
    e.preventDefault();
    const params = { search, category: category !== 'all' ? category : undefined, tags: selectedTags.length > 0 ? selectedTags : undefined };
    if (userLocation) {
      params.lat = userLocation[0];
      params.lng = userLocation[1];
      params.radius = radius;
    }
    fetchSales(params);
  };

  const detectLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const loc = [pos.coords.latitude, pos.coords.longitude];
          setUserLocation(loc);
          setMapCenter(loc);
          setMapZoom(11);
          fetchSales({ lat: loc[0], lng: loc[1], radius });
          // Save location for email notifications
          if (user) {
            axios.put(`${API}/users/location`, { lat: loc[0], lng: loc[1] }, { withCredentials: true })
              .then(() => {
                setNotificationsEnabled(true);
                toast.success('Location saved — you\'ll get alerts for nearby sales!');
              })
              .catch(() => {});
            // Also subscribe to push notifications
            if (pushSupported && !pushSubscribed) {
              pushSubscribe(loc[0], loc[1]);
            }
          }
        },
        () => alert('Could not detect your location. Please search by city or zip code.')
      );
    }
  };

  const toggleNotifications = async () => {
    if (!user) return;
    const newVal = !notificationsEnabled;
    try {
      await axios.put(`${API}/users/notifications`, { enabled: newVal }, { withCredentials: true });
      setNotificationsEnabled(newVal);
      toast.success(newVal ? 'Email notifications enabled' : 'Email notifications disabled');
    } catch {
      toast.error('Failed to update notification settings');
    }
  };

  const handleCategoryChange = (val) => {
    setCategory(val);
    const params = { category: val !== 'all' ? val : undefined, search, tags: selectedTags.length > 0 ? selectedTags : undefined };
    if (userLocation) { params.lat = userLocation[0]; params.lng = userLocation[1]; params.radius = radius; }
    fetchSales(params);
  };

  const handleRadiusChange = (val) => {
    setRadius(val);
    if (userLocation) {
      fetchSales({ lat: userLocation[0], lng: userLocation[1], radius: val, category: category !== 'all' ? category : undefined, search, tags: selectedTags.length > 0 ? selectedTags : undefined });
    }
  };

  const toggleTag = (tagName) => {
    setSelectedTags(prev => {
      const newTags = prev.includes(tagName) ? prev.filter(t => t !== tagName) : [...prev, tagName];
      const params = { category: category !== 'all' ? category : undefined, search, tags: newTags.length > 0 ? newTags : undefined };
      if (userLocation) { params.lat = userLocation[0]; params.lng = userLocation[1]; params.radius = radius; }
      fetchSales(params);
      return newTags;
    });
  };

  return (
    <div className="app-layout" data-testid="main-app">
      {/* List Pane */}
      <div className="list-pane" data-testid="list-pane">
        {/* Search Bar */}
        <form onSubmit={handleSearch} className="mb-4" data-testid="search-form">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <MagnifyingGlass size={18} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C7A99]" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="input-neo pl-10 py-2.5 text-sm"
                placeholder="Search city, zip, or keywords..."
                data-testid="search-location-input"
              />
            </div>
            <button type="submit" className="btn-primary py-2 px-4" data-testid="search-submit-btn">
              <MagnifyingGlass size={18} weight="bold" />
            </button>
          </div>
        </form>

        {/* Filter Row */}
        <div className="flex items-center gap-2 mb-3">
          <button onClick={detectLocation} className="btn-outline py-2 px-3 text-xs flex items-center gap-1" data-testid="detect-location-btn">
            <Crosshair size={16} weight="bold" />
            Near Me
          </button>
          <Select value={category} onValueChange={handleCategoryChange}>
            <SelectTrigger className="border-2 border-[#004E89] rounded-xl shadow-[2px_2px_0px_#004E89] h-9 text-xs font-bold w-[140px]" data-testid="category-filter">
              <SelectValue placeholder="All Types" />
            </SelectTrigger>
            <SelectContent className="border-2 border-[#004E89] rounded-xl shadow-[4px_4px_0px_#004E89]">
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="estate">Estate Sales</SelectItem>
              <SelectItem value="garage">Garage Sales</SelectItem>
              <SelectItem value="auction">Auctions</SelectItem>
              <SelectItem value="moving">Moving Sales</SelectItem>
              <SelectItem value="business_closing">Business Going Out</SelectItem>
              <SelectItem value="craft_fair">Craft Fairs</SelectItem>
              <SelectItem value="food_vendor">Food Vendors</SelectItem>
              <SelectItem value="carnival">Carnivals</SelectItem>
              <SelectItem value="vendor_event">Vendor Events</SelectItem>
            </SelectContent>
          </Select>
          <button onClick={() => setShowFilters(f => !f)} className="btn-outline py-2 px-3 text-xs flex items-center gap-1" data-testid="toggle-date-filter">
            <Calendar size={16} weight="bold" /> Dates
          </button>
          <button onClick={() => setShowTagFilter(f => !f)} className={`btn-outline py-2 px-3 text-xs flex items-center gap-1 ${selectedTags.length > 0 ? 'bg-[#FF6B35] text-white border-[#FF6B35]' : ''}`} data-testid="toggle-tag-filter">
            <Tag size={16} weight="bold" /> Items {selectedTags.length > 0 && `(${selectedTags.length})`}
          </button>
        </div>

        {/* Date Range Filter */}
        {showFilters && (
          <div className="mb-3 bg-white border-2 border-[#004E89] rounded-xl p-3 shadow-[2px_2px_0px_#004E89] flex items-center gap-3 flex-wrap" data-testid="date-filter-container">
            <div className="flex items-center gap-2">
              <label className="text-xs font-bold text-[#004E89]">From</label>
              <input type="date" value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); }} className="border-2 border-[#004E89] rounded-lg px-2 py-1 text-xs font-medium text-[#004E89]" data-testid="date-from-input" />
            </div>
            <div className="flex items-center gap-2">
              <label className="text-xs font-bold text-[#004E89]">To</label>
              <input type="date" value={dateTo} onChange={(e) => { setDateTo(e.target.value); }} className="border-2 border-[#004E89] rounded-lg px-2 py-1 text-xs font-medium text-[#004E89]" data-testid="date-to-input" />
            </div>
            {(dateFrom || dateTo) && (
              <button onClick={() => { setDateFrom(''); setDateTo(''); }} className="text-xs font-bold text-[#FF6B35] hover:underline" data-testid="clear-dates-btn">
                Clear
              </button>
            )}
          </div>
        )}

        {/* Item Tag Filter */}
        {showTagFilter && (
          <div className="mb-3 bg-white border-2 border-[#004E89] rounded-xl p-3 shadow-[2px_2px_0px_#004E89]" data-testid="tag-filter-container">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-bold text-[#004E89]">Filter by Items</span>
              {selectedTags.length > 0 && (
                <button onClick={() => { setSelectedTags([]); const params = { category: category !== 'all' ? category : undefined, search }; if (userLocation) { params.lat = userLocation[0]; params.lng = userLocation[1]; params.radius = radius; } fetchSales(params); }}
                  className="text-xs font-bold text-[#FF6B35] hover:underline" data-testid="clear-tags-btn">Clear All</button>
              )}
            </div>
            <div className="flex flex-wrap gap-1.5">
              {['Furniture', 'Antiques', 'Electronics', 'Clothing', 'Books', 'Tools', 'Kitchenware', 'Art', 'Jewelry', 'Toys', 'Sports', 'Other'].map(tag => (
                <button key={tag} onClick={() => toggleTag(tag)}
                  className={`px-2.5 py-1 rounded-lg text-xs font-bold border-2 transition-all ${selectedTags.includes(tag) ? 'bg-[#FF6B35] text-white border-[#004E89] shadow-[2px_2px_0px_#004E89]' : 'bg-[#F9F9F6] text-[#5C7A99] border-[#5C7A99]/30 hover:border-[#004E89]'}`}
                  data-testid={`tag-filter-${tag.toLowerCase()}`}>
                  {tag}
                  {availableTags.find(t => t.name === tag) && (
                    <span className="ml-1 opacity-60">({availableTags.find(t => t.name === tag)?.count})</span>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Distance Radius Slider */}
        {userLocation && (
          <div className="mb-4 bg-white border-2 border-[#004E89] rounded-xl p-3 shadow-[2px_2px_0px_#004E89]" data-testid="radius-slider-container">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-[10px] font-bold text-[#004E89] uppercase tracking-wide" style={{ fontFamily: 'Outfit' }}>Distance Radius</span>
              <span className="text-xs font-black text-[#FF6B35]" data-testid="radius-value">{radius} mi</span>
            </div>
            <input
              type="range"
              min="5"
              max="200"
              step="5"
              value={radius}
              onChange={(e) => handleRadiusChange(Number(e.target.value))}
              className="w-full h-2 rounded-full appearance-none cursor-pointer"
              style={{
                background: `linear-gradient(to right, #FF6B35 0%, #FF6B35 ${((radius - 5) / 195) * 100}%, #E5E7EB ${((radius - 5) / 195) * 100}%, #E5E7EB 100%)`,
              }}
              data-testid="radius-slider"
            />
            <div className="flex justify-between text-[9px] text-[#5C7A99] mt-1">
              <span>5 mi</span>
              <span>100 mi</span>
              <span>200 mi</span>
            </div>
          </div>
        )}

        {/* Email Notifications Toggle */}
        {user && userLocation && (
          <div className="mb-4 bg-white border-2 border-[#004E89] rounded-xl p-3 shadow-[2px_2px_0px_#004E89] flex items-center justify-between" data-testid="notification-toggle-container">
            <div className="flex items-center gap-2">
              {notificationsEnabled ? (
                <Bell size={18} weight="fill" className="text-[#FF6B35]" />
              ) : (
                <BellSlash size={18} weight="bold" className="text-[#5C7A99]" />
              )}
              <div>
                <span className="text-xs font-bold text-[#004E89] block" style={{ fontFamily: 'Outfit' }}>Sale Alerts</span>
                <span className="text-[10px] text-[#5C7A99]">Email me about nearby sales (100 mi)</span>
              </div>
            </div>
            <button
              onClick={toggleNotifications}
              className={`relative w-10 h-5 rounded-full border-2 border-[#004E89] transition-colors ${notificationsEnabled ? 'bg-[#4CB944]' : 'bg-gray-200'}`}
              data-testid="notification-toggle-btn"
            >
              <span className={`absolute top-0.5 w-3 h-3 rounded-full bg-white border border-[#004E89] transition-transform ${notificationsEnabled ? 'translate-x-5' : 'translate-x-0.5'}`} />
            </button>
          </div>
        )}

        {/* Results */}
        <div className="text-xs font-bold text-[#5C7A99] uppercase tracking-wide mb-3" data-testid="results-count">
          {loading ? 'Searching...' : `${sales.length} sale${sales.length !== 1 ? 's' : ''} found`}
        </div>

        <div className="flex flex-col gap-4">
          {(() => {
            const filtered = sales.filter(s => {
              if (dateFrom && s.end_date < dateFrom) return false;
              if (dateTo && s.start_date > dateTo) return false;
              return true;
            });
            if (filtered.length === 0 && !loading) return (
              <div className="text-center py-12" data-testid="no-results">
                <MapPin size={48} weight="duotone" className="mx-auto text-[#5C7A99] mb-4" />
                <p className="text-[#5C7A99] font-medium">No sales found{dateFrom || dateTo ? ' for these dates' : ' in this area'}</p>
                <p className="text-[#5C7A99] text-sm mt-1">Try a different search or check back later</p>
              </div>
            );
            return filtered.map((sale, i) => (
              <div key={sale.id} style={{ animationDelay: `${i * 0.05}s` }}>
                <SaleCard
                  sale={sale}
                  onHover={setHoveredSale}
                  isActive={hoveredSale?.id === sale.id}
                  isSaved={savedIds.has(sale.id)}
                  onToggleSave={user ? toggleSaveCard : null}
                />
              </div>
            ));
          })()}
        </div>
      </div>

      {/* Map Pane */}
      <div className="map-pane" data-testid="map-pane">
        <MapContainer center={mapCenter} zoom={mapZoom} style={{ width: '100%', height: '100%' }} zoomControl={false}>
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <MapController center={mapCenter} zoom={mapZoom} />
          {sales.filter(s => s.lat && s.lng && !s.address_hidden).map(sale => (
            <Marker
              key={sale.id}
              position={[sale.lat, sale.lng]}
              icon={getMarkerIcon(sale.category)}
            >
              <Popup>
                <div className="p-1 min-w-[180px]">
                  <div className="font-bold text-[#004E89] text-sm mb-1" style={{ fontFamily: 'Outfit' }}>{sale.title}</div>
                  <div className="text-xs text-[#5C7A99] mb-1">{sale.address}</div>
                  <div className="text-xs text-[#5C7A99] mb-2">{sale.city}, {sale.state}</div>
                  <Link to={`/sale/${sale.id}`} className="text-[#FF6B35] text-xs font-bold hover:underline" data-testid={`map-popup-link-${sale.id}`}>
                    View Details →
                  </Link>
                </div>
              </Popup>
            </Marker>
          ))}
          {userLocation && (
            <Marker position={userLocation} icon={L.divIcon({
              className: 'custom-marker',
              html: `<div style="width:16px;height:16px;background:#004E89;border:3px solid white;border-radius:50%;box-shadow:0 0 8px rgba(0,78,137,0.5)"></div>`,
              iconSize: [16, 16],
              iconAnchor: [8, 8],
            })}>
              <Popup>Your Location</Popup>
            </Marker>
          )}
        </MapContainer>
      </div>
    </div>
  );
}
